import { useState, useCallback, useEffect, useRef } from 'react';
import { generateImage as apiGenerateImage, editImage as apiEditImage, base64ToDataUrl } from '../api/aiImageApi';
import type {
  ModalScreen,
  SelectionTabId,
  SelectedImageData,
  ImageInsertData,
  ExistingImageData,
} from './imageInsertTypes';
import type { UploadPanelState } from './UploadPanel';
import { createInitialUploadState } from './UploadPanel';
import type { AIGenerateState } from './AIGeneratePanel';
import { createInitialAIGenerateState } from './AIGeneratePanel';
import type { AIEditState } from './AIEditPanel';
import { createInitialAIEditState } from './AIEditPanel';
import type { LibraryPanelState } from './libraryPanelTypes';
import { createInitialLibraryState } from './libraryPanelTypes';
import { toScopeAccountId, useClientContext } from '../hooks/useClientContext';
import { getAccountLabel } from './layout/ClientContextPicker';

interface ModalState {
  screen: ModalScreen;
  previousScreen: ModalScreen | null;
  activeTab: SelectionTabId;
  selectedImage: SelectedImageData | null;
  pendingRemoval: boolean;
  uploadState: UploadPanelState;
  aiGenerateState: AIGenerateState;
  aiEditState: AIEditState;
  libraryState: LibraryPanelState;
}

function createInitialState(
  initialImage: ExistingImageData | undefined,
  scopeAccountId: number | null,
  scopeAccountName: string | null
): ModalState {
  return {
    screen: initialImage?.src ? 'existing-image' : 'image-selection',
    previousScreen: null,
    activeTab: 'upload',
    selectedImage: null,
    pendingRemoval: false,
    uploadState: createInitialUploadState(),
    aiGenerateState: createInitialAIGenerateState(),
    aiEditState: createInitialAIEditState(),
    libraryState: createInitialLibraryState(scopeAccountId, scopeAccountName),
  };
}

interface UseImageInsertModalControllerProps {
  isOpen: boolean;
  onInsert: (data: ImageInsertData) => void;
  onClose: () => void;
  targetAspectRatio?: string;
  initialImage?: ExistingImageData;
}

export function useImageInsertModalController({
  isOpen,
  onInsert,
  onClose,
  targetAspectRatio,
  initialImage,
}: UseImageInsertModalControllerProps) {
  // Library scope comes from the global header account context; seeded on every
  // open (state resets below), so each modal session reflects the current context.
  const { clientContext } = useClientContext();
  const scopeAccountId = toScopeAccountId(clientContext?.id);
  const scopeAccountName = clientContext && scopeAccountId !== null ? getAccountLabel(clientContext) : null;

  const [state, setState] = useState<ModalState>(() =>
    createInitialState(initialImage, scopeAccountId, scopeAccountName)
  );
  const initialImageRef = useRef(initialImage);
  // Tracks the live editing session so the effect below can tell reopening
  // (always reinitialize) apart from prop churn while open.
  const sessionRef = useRef<{ open: boolean; scopeAccountId: number | null }>({
    open: false,
    scopeAccountId,
  });

  // Each modal opening is one stable editing session: initialize on open, then
  // reinitialize only when the source image or account scope genuinely changes.
  // `initialImage` is compared by value, not reference — call sites recreate
  // `{src, alt}` on every parent render (e.g. during lesson autosave), and a
  // reference-only change must not discard in-progress AI work.
  useEffect(() => {
    if (!isOpen) {
      const wasOpen = sessionRef.current.open;
      // Reset browsing on close and on account changes while closed, before
      // the child can start a request with a previous session's parameters.
      setState((prev) => !wasOpen && prev.libraryState.accountId === scopeAccountId
        && prev.libraryState.accountName === scopeAccountName ? prev : ({
          ...prev,
          libraryState: createInitialLibraryState(scopeAccountId, scopeAccountName),
        }));
      sessionRef.current.open = false;
      return;
    }
    const captured = initialImageRef.current;
    const sourceChanged =
      (captured?.src ?? '') !== (initialImage?.src ?? '') ||
      (captured?.alt ?? '') !== (initialImage?.alt ?? '');
    const scopeChanged = sessionRef.current.scopeAccountId !== scopeAccountId;
    if (sessionRef.current.open && !sourceChanged && !scopeChanged) {
      return;
    }
    const isNewSession = !sessionRef.current.open;
    sessionRef.current = { open: true, scopeAccountId };
    initialImageRef.current = initialImage;
    setState((prev) => {
      const next = createInitialState(initialImage, scopeAccountId, scopeAccountName);
      if (isNewSession && prev.libraryState.accountId === scopeAccountId) {
        // A child's first request may already have started in this commit.
        next.libraryState = prev.libraryState;
      } else if (!isNewSession) {
        next.libraryState.hasRequested = prev.libraryState.hasRequested;
      }
      return next;
    });
  }, [isOpen, initialImage, scopeAccountId, scopeAccountName]);

  const setScreen = useCallback((screen: ModalScreen) => {
    setState((prev) => ({
      ...prev,
      screen,
      previousScreen: prev.screen,
    }));
  }, []);

  // --- Screen transitions ---

  const handleReplace = useCallback(() => {
    setScreen('image-selection');
  }, [setScreen]);

  const handleEditWithAI = useCallback(() => {
    setState((prev) => ({
      ...prev,
      screen: 'ai-edit',
      previousScreen: prev.screen,
      aiEditState: createInitialAIEditState(),
    }));
  }, []);

  const handleRemoveImage = useCallback(() => {
    setState((prev) => ({
      ...prev,
      screen: 'image-selection',
      previousScreen: prev.screen,
      pendingRemoval: true,
    }));
  }, []);

  const handleConfirmRemoval = useCallback(() => {
    onInsert({ src: '', alt: '' });
    onClose();
  }, [onInsert, onClose]);

  const handleBack = useCallback(() => {
    setState((prev) => ({
      ...prev,
      screen: prev.previousScreen || 'image-selection',
      previousScreen: null,
      selectedImage: null,
    }));
  }, []);

  const handleTabChange = useCallback((tab: SelectionTabId) => {
    setState((prev) => ({ ...prev, activeTab: tab }));
  }, []);

  // --- Image ready (from any source) -> alt text step ---

  const handleImageReady = useCallback((data: SelectedImageData) => {
    setState((prev) => ({
      ...prev,
      screen: 'alt-text-step',
      previousScreen: prev.screen,
      selectedImage: data,
    }));
  }, []);

  // --- State change handlers for sub-panels ---

  const handleUploadStateChange = useCallback((uploadState: UploadPanelState) => {
    setState((prev) => ({ ...prev, uploadState }));
  }, []);

  const handleAIGenerateStateChange = useCallback((aiGenerateState: AIGenerateState) => {
    setState((prev) => ({ ...prev, aiGenerateState }));
  }, []);

  const handleAIEditStateChange = useCallback((aiEditState: AIEditState) => {
    setState((prev) => ({ ...prev, aiEditState }));
  }, []);

  const handleLibraryStateChange = useCallback((libraryState: LibraryPanelState) => {
    setState((prev) => ({ ...prev, libraryState }));
  }, []);

  // --- AI generate handler ---

  const handleGenerate = useCallback(
    async (prompt: string): Promise<{ imageUrl: string; mimeType: string }> => {
      const response = await apiGenerateImage({ prompt, aspectRatio: targetAspectRatio });
      const imageUrl = base64ToDataUrl(response.imageData, response.mimeType);
      return { imageUrl, mimeType: response.mimeType };
    },
    [targetAspectRatio]
  );

  // --- AI edit handler ---

  const handleAIEdit = useCallback(
    async (prompt: string): Promise<{ imageUrl: string; mimeType: string }> => {
      const currentImage = initialImageRef.current;
      if (!currentImage?.src) {
        throw new Error('No source image to edit');
      }

      const result = await apiEditImage({ imageUrl: currentImage.src, prompt, aspectRatio: targetAspectRatio });
      const imageUrl = base64ToDataUrl(result.imageData, result.mimeType);
      return { imageUrl, mimeType: result.mimeType };
    },
    [targetAspectRatio]
  );

  // --- Final insert from alt text step ---

  const handleInsert = useCallback(
    (data: ImageInsertData) => {
      onInsert(data);
    },
    [onInsert]
  );

  // --- Title based on screen ---

  const getTitle = useCallback((): string => {
    switch (state.screen) {
      case 'existing-image':
        return 'Manage Image';
      case 'image-selection':
        return 'Insert Image';
      case 'alt-text-step':
        return 'Image Details';
      case 'ai-edit':
        return 'Edit with AI';
    }
  }, [state.screen]);

  return {
    state,
    scopeAccountId,
    scopeAccountName,
    getTitle,
    handleReplace,
    handleEditWithAI,
    handleRemoveImage,
    handleConfirmRemoval,
    handleBack,
    handleTabChange,
    handleImageReady,
    handleUploadStateChange,
    handleAIGenerateStateChange,
    handleAIEditStateChange,
    handleLibraryStateChange,
    handleGenerate,
    handleAIEdit,
    handleInsert,
    onClose,
  };
}
